// src/hooks/usePermissions.js
import { useAuth } from "./useAuth";
import { useCollection } from "./useCollection";

export const usePermissions = () => {
  const { userProfile } = useAuth();
  const { data: roles } = useCollection("roles");

  /**
   * Verifica si el usuario tiene acceso a un módulo específico
   * @param {string} moduleId - ID del módulo (ej: "sales", "contacts", "settings")
   * @param {string} permission - Tipo de permiso (ej: "view", "create", "edit", "delete")
   * @returns {boolean}
   */
  const hasPermission = (moduleId, permission = "view") => {
    // Si no hay usuario o roles, denegar acceso
    if (!userProfile || !roles) return false;

    // Encontrar el rol del usuario
    const userRole = roles.find(r => r.id === userProfile.role);
    
    // Si no se encuentra el rol, denegar acceso
    if (!userRole) return false;

    // Verificar si el módulo existe en los permisos del rol
    if (!userRole.modules || !userRole.modules[moduleId]) return false;

    // Verificar el permiso específico
    return userRole.modules[moduleId][permission] === true;
  };

  /**
   * Verifica si el usuario tiene acceso a alguno de los módulos
   * @param {string[]} moduleIds - Array de IDs de módulos
   * @param {string} permission - Tipo de permiso
   * @returns {boolean}
   */
  const hasAnyPermission = (moduleIds, permission = "view") => {
    return moduleIds.some(moduleId => hasPermission(moduleId, permission));
  };

  /**
   * Verifica si el usuario tiene acceso a todos los módulos
   * @param {string[]} moduleIds - Array de IDs de módulos
   * @param {string} permission - Tipo de permiso
   * @returns {boolean}
   */
  const hasAllPermissions = (moduleIds, permission = "view") => {
    return moduleIds.every(moduleId => hasPermission(moduleId, permission));
  };

  /**
   * Obtiene todos los módulos a los que el usuario tiene acceso
   * @param {string} permission - Tipo de permiso
   * @returns {string[]}
   */
  const getAccessibleModules = (permission = "view") => {
    if (!userProfile || !roles) return [];
    
    const userRole = roles.find(r => r.id === userProfile.role);
    if (!userRole || !userRole.modules) return [];

    return Object.keys(userRole.modules).filter(
      moduleId => userRole.modules[moduleId][permission] === true
    );
  };

  return {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    getAccessibleModules,
    userRole: userProfile?.role,
    roleData: roles?.find(r => r.id === userProfile?.role),
  };
};